import { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, RefreshCw, Search, Clock3, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const FILTERS = ['', 'payment_pending', 'confirmed', 'in_progress', 'completed', 'cancelled'];

export default function AdminOperations() {
  const [bookings, setBookings] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (nextStatus) => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/operations', { params: { status: nextStatus, limit: 200 } });
      setBookings(data.bookings || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load live operations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(status);
    const timer = window.setInterval(() => load(status), 15000);
    return () => window.clearInterval(timer);
  }, [load, status]);

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <div className="admin-eyebrow"><Activity size={13} /> Live operations</div>
          <h1>Operations</h1>
          <p>A plain-language queue for what is happening right now across RESQLY.</p>
        </div>
        <button className="admin-lux-button secondary" onClick={() => load()} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="admin-filter-row px-0">
        {FILTERS.map((value) => (
          <button key={value || 'all'} className={status === value ? 'active' : ''} onClick={() => setStatus(value)}>
            {value ? value.replaceAll('_', ' ') : 'All'}
          </button>
        ))}
      </div>

      <div className="admin-card mt-3">
        <div className="admin-card-head">
          <div><h2>Booking queue</h2><p>{bookings.length} visible operations</p></div>
          <span className="admin-live-tag"><span className="admin-live-dot" /> Live</span>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Request</th><th>Service</th><th>Customer</th><th>Provider</th><th>Status</th><th>Created</th></tr></thead>
            <tbody>
              {bookings.map((booking, index) => {
                const done = ['completed', 'delivered'].includes(booking.status);
                const warning = ['cancelled', 'rejected'].includes(booking.status);
                return (
                  <motion.tr key={booking.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.012 }}>
                    <td><strong>{booking.id?.slice(0, 8) || '—'}</strong></td>
                    <td>{booking.service_key || booking.service || '—'}</td>
                    <td>{booking.consumer?.name || booking.consumer_id?.slice(0, 8) || '—'}</td>
                    <td>{booking.provider?.name || booking.provider_id?.slice(0, 8) || '—'}</td>
                    <td>
                      <span className={`admin-status ${done ? 'success' : warning ? 'danger' : 'warning'}`}>
                        {done ? <CheckCircle2 size={10} /> : warning ? <AlertTriangle size={10} /> : <Clock3 size={10} />}
                        {booking.status || 'unknown'}
                      </span>
                    </td>
                    <td>{booking.created_at ? new Date(booking.created_at).toLocaleString() : '—'}</td>
                  </motion.tr>
                );
              })}
              {!loading && bookings.length === 0 && (
                <tr><td colSpan="6"><div className="admin-empty">No operations in this filter.</div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
